import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEO from '../components/common/SEO';
import Layout from '../components/layout/Layout';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import styled from 'styled-components';
import siteConfig from '../config/site';

const WHATSAPP_NUMBER = siteConfig.contact.whatsapp;
const NOTIFY_EMAIL = siteConfig.contact.orderEmail;

// Plan lookup keyed by the /payment/:plan slug used on the pricing cards.
// Keep these in sync with the pricingPlans array in src/components/home/Pricing.tsx.
const PLANS: Record<string, { name: string; price: string }> = {
  demo: { name: 'Demo License', price: '$15' },
  basic: { name: 'Basic License (Yearly)', price: '$2,500' },
  pro: { name: 'Pro License (Lifetime)', price: '$5,000' },
};

const WALLETS = siteConfig.payment.wallets;

const PaymentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PlanSummary = styled.div`
  background-color: var(--background-light);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  h3 {
    margin: 0;
    font-size: 1.25rem;
    color: var(--text-color);
  }

  .price {
    font-size: 1.75rem;
    font-weight: bold;
    color: var(--primary-color);
  }

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FormSection = styled.div`
  background-color: var(--background-light);
  border-radius: var(--border-radius);
  padding: 2rem;
  box-shadow: var(--box-shadow);
  margin-bottom: 2rem;

  h3 {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
    color: var(--text-color);
  }
`;

const PaymentOptions = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const PaymentOption = styled.div<{ selected: boolean }>`
  flex: 1;
  border: 2px solid ${({ selected }) => (selected ? 'var(--primary-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius);
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: ${({ selected }) => (selected ? 'var(--primary-color-light)' : 'var(--background)')};

  &:hover {
    border-color: var(--primary-color);
  }

  .payment-header {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;

    svg {
      width: 30px;
      height: 30px;
      margin-right: 1rem;
      color: var(--primary-color);
    }

    h4 {
      font-size: 1.125rem;
      margin: 0;
      color: var(--text-color);
    }
  }

  p {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin: 0;
  }
`;

const CryptoPayment = styled.div`
  text-align: center;
  margin-bottom: 0;

  .qr-container {
    background-color: white;
    padding: 1.5rem;
    border-radius: var(--border-radius);
    display: inline-block;
    margin-bottom: 1.5rem;

    img {
      width: 200px;
      height: 200px;
      display: block;
    }
  }

  .address-container {
    background-color: var(--background);
    padding: 1rem 3.5rem 1rem 1rem;
    border-radius: var(--border-radius);
    margin-bottom: 1.5rem;
    word-break: break-all;
    font-family: monospace;
    font-size: 0.875rem;
    color: var(--text-color);
    position: relative;

    button {
      position: absolute;
      right: 0.5rem;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: var(--primary-color);
      cursor: pointer;
      font-size: 1rem;

      &:hover {
        color: var(--primary-color-dark);
      }
    }
  }

  .amount-container {
    font-size: 1.25rem;
    font-weight: bold;
    margin-bottom: 1.5rem;
    color: var(--text-color);
  }

  .instructions {
    text-align: left;
    margin-bottom: 2rem;

    h4 {
      font-size: 1.125rem;
      margin-bottom: 1rem;
      color: var(--text-color);
    }

    ol {
      padding-left: 1.5rem;

      li {
        margin-bottom: 0.5rem;
        line-height: 1.7;
        color: var(--text-secondary);
      }
    }
  }
`;

const NotifyHeading = styled.h4`
  font-size: 1.125rem;
  margin-bottom: 1rem;
  color: var(--text-color);
  text-align: center;
`;

const NotifyButtons = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 2rem 1rem 0;

  svg {
    width: 64px;
    height: 64px;
    color: var(--primary-color);
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
    color: var(--text-color);
  }

  p {
    font-size: 1rem;
    color: var(--text-secondary);
    margin-bottom: 1.5rem;
    max-width: 520px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const PaymentPage: React.FC = () => {
  const { plan } = useParams<{ plan: string }>();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState<'bitcoin' | 'usdt'>('usdt');
  const [copied, setCopied] = useState(false);
  const [notified, setNotified] = useState(false);

  const selected = (plan && PLANS[plan]) || { name: 'Flash USDT Website License', price: '' };
  const address = WALLETS[paymentMethod];
  const methodLabel = paymentMethod === 'bitcoin' ? 'Bitcoin (BTC)' : 'USDT (TRC20)';

  const orderText =
    `Hello Flash USDT Website! I have completed payment for the ${selected.name}` +
    `${selected.price ? ` (${selected.price})` : ''} via ${methodLabel}. ` +
    `Please activate my license.`;

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(orderText)}`;
  const emailLink =
    `mailto:${NOTIFY_EMAIL}?subject=${encodeURIComponent(`Payment completed - ${selected.name}`)}` +
    `&body=${encodeURIComponent(orderText)}`;

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(address).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Layout>
      <SEO
        title={`Buy ${selected.name} | Flash USDT Website`}
        description="Complete your purchase of Flash USDT Website. Pay with Bitcoin or USDT (TRC20), then notify us on WhatsApp or by email to activate your license."
        keywords="buy USDT flasher, license, crypto payment, bitcoin, USDT, checkout"
      />

      <Section
        title="Buy License"
        subtitle="Pay with crypto, then let us know so we can activate your license."
        background="light"
        padding="large"
      >
        <PaymentContainer>
          <PlanSummary>
            <h3>{selected.name}</h3>
            {selected.price && <div className="price">{selected.price}</div>}
          </PlanSummary>

          <FormSection>
            <h3>Select Payment Method</h3>

            <PaymentOptions>
              <PaymentOption
                selected={paymentMethod === 'bitcoin'}
                onClick={() => setPaymentMethod('bitcoin')}
              >
                <div className="payment-header">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.548v-.002zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.33-.525 2.107c-.345-.087-.705-.167-1.064-.25l.526-2.127-1.32-.33-.54 2.165c-.285-.067-.565-.132-.84-.2l-1.815-.45-.35 1.407s.975.225.955.236c.535.136.63.486.615.766l-1.477 5.92c-.075.166-.24.406-.614.314.015.02-.96-.24-.96-.24l-.66 1.51 1.71.426.93.242-.54 2.19 1.32.327.54-2.17c.36.1.705.19 1.05.273l-.51 2.154 1.32.33.545-2.19c2.24.427 3.93.257 4.64-1.774.57-1.637-.03-2.58-1.217-3.196.854-.193 1.5-.76 1.68-1.93h.01zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.18 3.137.524 2.75 2.084v.006z" />
                  </svg>
                  <h4>Bitcoin</h4>
                </div>
                <p>Pay with Bitcoin (BTC)</p>
              </PaymentOption>

              <PaymentOption
                selected={paymentMethod === 'usdt'}
                onClick={() => setPaymentMethod('usdt')}
              >
                <div className="payment-header">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 24c6.627 0 12-5.373 12-12S18.627 0 12 0 0 5.373 0 12s5.373 12 12 12z" />
                    <path d="M13.603 8.747v1.25h2.154v1.86h-2.154v1.25h2.154v3.732h-2.154v1.25h-1.25v-1.25H8.243v-3.732h4.11v-1.25h-4.11v-1.86h4.11v-1.25h-4.11V6.875h4.11v-1.25h1.25v1.25h2.154v1.872h-2.154z" fill="#fff" />
                  </svg>
                  <h4>USDT (TRC20)</h4>
                </div>
                <p>Pay with Tether USD (USDT) on the TRON network</p>
              </PaymentOption>
            </PaymentOptions>

            <CryptoPayment>
              <div className="qr-container">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${address}`}
                  alt={`${paymentMethod === 'bitcoin' ? 'Bitcoin' : 'USDT TRC20'} QR Code`}
                />
              </div>

              <div className="address-container">
                {address}
                <button onClick={handleCopyAddress}>{copied ? '✓' : 'Copy'}</button>
              </div>

              <div className="amount-container">Please Make Exact Payment</div>

              <div className="instructions">
                <h4>Payment Instructions:</h4>
                <ol>
                  <li>Send the exact amount for your plan to the address above.</li>
                  <li>Use the {paymentMethod === 'bitcoin' ? 'Bitcoin' : 'TRON (TRC20)'} network for your transaction.</li>
                  <li>After paying, notify us using one of the buttons below so we can activate your license.</li>
                  <li>We'll send your license key to your contact address after confirming the payment.</li>
                </ol>
              </div>

              {!notified ? (
                <>
                  <NotifyHeading>I've completed payment — notify us:</NotifyHeading>
                  <NotifyButtons>
                    <Button
                      variant="primary"
                      fullWidth
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setNotified(true)}
                    >
                      Notify on WhatsApp
                    </Button>
                    <Button
                      variant="outline"
                      fullWidth
                      href={emailLink}
                      onClick={() => setNotified(true)}
                    >
                      Notify by Email
                    </Button>
                  </NotifyButtons>
                </>
              ) : (
                <SuccessMessage>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                    <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z" />
                  </svg>
                  <h3>Thank you!</h3>
                  <p>
                    We've received your payment notification for the {selected.name}. Our team will
                    verify the transaction and activate your license, typically within 1–24 hours.
                  </p>
                  <Button variant="primary" onClick={() => navigate('/')}>
                    Return to Home
                  </Button>
                </SuccessMessage>
              )}
            </CryptoPayment>
          </FormSection>
        </PaymentContainer>
      </Section>
    </Layout>
  );
};

export default PaymentPage;
